import { useState, useCallback } from 'react';

import { supabase } from '../../../lib/supabase';
import type { GameRoom, Category } from '../../../types/multiplayer';

interface UseGameRoomDataOptions {
  roomId: string;
  userId: string | null;
}

export function useGameRoomData({ roomId, userId }: UseGameRoomDataOptions) {
  const [currentRoom, setCurrentRoom] = useState<GameRoom | null>(null);
  const [isHost, setIsHost] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch room data - memoized with useCallback to prevent recreation on every render
  const fetchRoomData = useCallback(async () => {
    if (!roomId) return;
    try {
      setIsLoading(true);
      // Get room data, including family_name if family_id is present
      const { data: roomData, error: roomError } = await supabase
        .from('multiplayer_game_rooms')
        .select('*, families:family_id(name), friends_groups:friends_group_id(name)')
        .eq('id', roomId)
        .single();
      if (roomError) throw roomError;
      if (!roomData) throw new Error('Room not found');

      // Extract family_name if present
      let family_name = undefined;
      let friends_group_name = undefined;
      const family_id = roomData.family_id;
      const friends_group_id = roomData.friends_group_id;
      if (roomData.families && typeof roomData.families.name === 'string') {
        family_name = roomData.families.name;
      }
      if (roomData.friends_groups && typeof roomData.friends_groups.name === 'string') {
        friends_group_name = roomData.friends_groups.name;
      }
      // Propagate family_id and family_name
      setCurrentRoom({
        ...roomData,
        family_id,
        family_name,
        friends_group_id,
        friends_group_name,
      } as GameRoom);
      // Check if current user is the host
      setIsHost(Boolean(userId && roomData.host_id === userId));
      // Get categories if needed
      if (roomData.category_id) {
        const { data: categoryData, error: categoryError } = await supabase
          .from('categories')
          .select('*')
          .in('id', Array.isArray(roomData.category_id) ? roomData.category_id : [roomData.category_id]);
        if (categoryError) throw categoryError;
        setCategories(categoryData || []);
      }
      return roomData;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch room data';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [roomId, userId]);

  return {
    currentRoom,
    setCurrentRoom,
    isHost,
    setIsHost,
    categories,
    setCategories,
    error,
    setError,
    isLoading,
    setIsLoading,
    fetchRoomData,
  };
}
